import { useCallback, useEffect } from 'react';
import { DataTestId, SearchType, TextKey } from '@mews-commander/enums';
import { AccountType } from '@mews-commander/data-access';
import { SearchResult } from '@mews-commander/types';
import { LocalizedSubsectionTitle } from '@mews-commander/ui';
import { LocalizedText, useSafeContext, useScope } from '@mews/framework';
import { compactArray } from '@mews/fp';
import { Container } from '@optimus-web/core';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import { getCustomer } from '@mews-commander/legacy-entities/src/ducks';
import {
    isCurrentProfileEnabledToManageCustomers,
    isCurrentProfileEnabledToManageCompanies,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { selectItemAssignmentAccount } from '../../ducks/itemAssignment';
import { SearchField, preselectSearchResult } from '../../../search';
import { clearSearchResults, loadSearchResult, resetSearch, selectSearchResult } from '../../../search/actions';
import { openNewEntityBackendDialog } from '../../../app/components/SearchNewEntityDialogBackendPage';
import { enhanceCustomerCreateUrl } from '../../../search/actions/utils';
import { SelectedAccountIdsContext } from './context';

interface AnotherAccountSearchProps {
    isCompany: boolean;
    defaultAccountIds: string[];
}

export const AnotherAccountSearch = ({ isCompany, defaultAccountIds }: AnotherAccountSearchProps) => {
    const dispatch = useCommanderThunkDispatch();
    const { selectedAccountId, setSelectedAccountId, billingOwnerId } = useSafeContext(SelectedAccountIdsContext);
    const scope = useScope() ?? '';

    const billingOwnerCompanyId = useCommanderSelector((state) => {
        const isCustomer = !isAccountIdCompany(state, { accountId: billingOwnerId });

        if (isCustomer) {
            const customer = getCustomer(state, { id: billingOwnerId });
            return customer?.companyId;
        }
    });

    const selectAccount = useCallback(
        ({ id, url, searchType }: SearchResult) => {
            if (!id && url) {
                let finalUrl = url;
                if (searchType === SearchType.Customer) {
                    finalUrl = enhanceCustomerCreateUrl(url);
                }

                const onSuccess = (data: { id: string }) => {
                    const { id: newEntityId } = data;
                    if (searchType === SearchType.Customer) {
                        dispatch(loadSearchResult({ searchType, result: [data] }, scope));
                    }
                    void dispatch(preselectSearchResult({ searchTypes: [searchType], id: newEntityId }, scope));
                    dispatch(selectItemAssignmentAccount({ accountId: newEntityId, scope }));
                    setSelectedAccountId(newEntityId);
                };

                openNewEntityBackendDialog({
                    searchType,
                    url: finalUrl,
                    onSuccess,
                });
            } else {
                dispatch(selectItemAssignmentAccount({ accountId: id, scope }));
                setSelectedAccountId(id);
            }
        },
        [dispatch, scope, setSelectedAccountId]
    );

    useEffect(() => {
        if (billingOwnerCompanyId && isCompany) {
            void dispatch(
                preselectSearchResult({ searchTypes: [SearchType.Company], id: billingOwnerCompanyId }, scope)
            );
            selectAccount({ id: billingOwnerCompanyId, searchType: SearchType.Company });
        }
        return () => {
            setSelectedAccountId('');
            dispatch(clearSearchResults(scope));
            dispatch(resetSearch(scope));
        };
    }, [billingOwnerCompanyId, dispatch, scope, isCompany, selectAccount, setSelectedAccountId]);

    const except = compactArray([...defaultAccountIds, selectedAccountId]);

    const deselectAccount = () => {
        setSelectedAccountId('');
        dispatch(clearSearchResults(scope));
        dispatch(resetSearch(scope));
    };

    const newEntityAllowed = isCompany
        ? isCurrentProfileEnabledToManageCompanies
        : isCurrentProfileEnabledToManageCustomers;

    return (
        <SearchField
            label={
                <Container padding={['100', '0']}>
                    <LocalizedSubsectionTitle
                        textKey={isCompany ? TextKey.SearchForAnotherCompany : TextKey.SearchForAnotherCustomer}
                    />
                </Container>
            }
            data-test-id={DataTestId.AnotherAccountSearch}
            key={isCompany ? AccountType.Company : AccountType.Customer}
            scope={scope}
            size="medium"
            except={except}
            name="accountId"
            placeholder={<LocalizedText textKey={isCompany ? TextKey.SearchCompanyName : TextKey.SearchCustomerName} />}
            searchTypes={[isCompany ? SearchType.Company : SearchType.Customer]}
            onResultClick={(result) => {
                dispatch(selectSearchResult({ selectedResult: result }, scope));
                selectAccount(result);
            }}
            onResultClear={deselectAccount}
            showInvoiceableTag={isCompany}
            newEntity={newEntityAllowed}
        />
    );
};
